import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import type { PartBrand, PartCategory, SparePart } from "@oriental-ramirez/shared";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";

export async function getAllParts(): Promise<SparePart[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.PARTS));
  return snapshot.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    return { ...data, id: d.id } as SparePart;
  });
}

export async function getPartById(id: string): Promise<SparePart | null> {
  const ref = doc(db, COLLECTIONS.PARTS, id);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as SparePart) : null;
}

export async function addPart(part: Omit<SparePart, "id">): Promise<SparePart> {
  const docRef = doc(collection(db, COLLECTIONS.PARTS));
  const withId = { ...part, id: docRef.id };
  await setDoc(docRef, withId);
  return withId as SparePart;
}

export async function updatePart(id: string, data: Partial<SparePart>): Promise<void> {
  const ref = doc(db, COLLECTIONS.PARTS, id);
  await updateDoc(ref, { ...data, id });
}

export async function deletePart(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.PARTS, id));
}

export async function getAllPartCategories(): Promise<PartCategory[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.PART_CATEGORIES));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as PartCategory));
}

export async function addPartCategory(category: Omit<PartCategory, "id">): Promise<string> {
  const docRef = doc(collection(db, COLLECTIONS.PART_CATEGORIES));
  const withId = { ...category, id: docRef.id };
  await setDoc(docRef, withId);
  return docRef.id;
}

export async function updatePartCategory(id: string, data: Partial<PartCategory>): Promise<void> {
  const ref = doc(db, COLLECTIONS.PART_CATEGORIES, id);
  await updateDoc(ref, { ...data, id });
}

export async function deletePartCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.PART_CATEGORIES, id));
}

export async function getAllPartBrands(): Promise<PartBrand[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.PART_BRANDS));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as PartBrand));
}

export async function addPartBrand(brand: Omit<PartBrand, "id">): Promise<string> {
  const docRef = doc(collection(db, COLLECTIONS.PART_BRANDS));
  const withId = { ...brand, id: docRef.id };
  await setDoc(docRef, withId);
  return docRef.id;
}

export async function updatePartBrand(id: string, data: Partial<PartBrand>): Promise<void> {
  const ref = doc(db, COLLECTIONS.PART_BRANDS, id);
  await updateDoc(ref, { ...data, id });
}

export async function deletePartBrand(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.PART_BRANDS, id));
}
